import { lazy, Suspense } from "react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { onGlobalQueryError } from "@/app/globalQueryError";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { showsReleaseNotes } from "@/shared/brand/bootstrap";
import { useBrand } from "@/shared/brand/store";
import { useTranslation } from "react-i18next";
// V23 Session-2 Epic 5 — react-i18next 单例 init(放 App 顶层而非 main.tsx,保持 main.tsx 精简)
import "@/shared/lib/i18n";
// 2026-04-30 toast 统一为 shadcn(@/shared/hooks/use-toast),sonner Toaster 已撤
import { Toaster } from "@/shared/ui/toaster";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { ConfirmProvider } from "@/shared/hooks/use-confirm";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/app/guards/ProtectedRoute";
import { AdminLayout } from "@/app/layouts/AdminLayout";
import { PageErrorBoundary } from "@/shared/ui/PageErrorFallback";
import { TopLoader } from "@/shared/ui/TopLoader";
import { PARTNER_PAGES } from "@/entities/partner";
// V25 (2026-05-10) — P-5: 高频路由 (Index/Login/AI 模块/海报核心) eager 保证首屏不闪 Suspense fallback。
//   admin / platform / billing / 模板库 / LLM 配置 — 90% 租户用户从不打开,改 React.lazy 切 chunk 减首屏 JS。
import Index from "./pages/Index.tsx";
import ClusterPage from "./pages/workbench/ClusterPage.tsx";
import NotFound from "./pages/NotFound.tsx";
import AiKefu from "@/features/customer-service/ui/AiKefuPage";
import AiPeilian from "@/features/coaching/ui/AiPeilianPage";
import ChannelManagement from "@/features/channels/ui/ChannelManagementPage";
import Login from "@/features/auth/ui/LoginPage";
import LegalDocPage from "@/features/legal/ui/LegalDocPage";
import WeComCoaching from "./wecom-h5/coaching/WeComCoaching.tsx";
import KnowledgeBase from "@/features/knowledge-base/ui/KnowledgeBasePage";
import PosterDashboardPage from "@/features/poster/ui/PosterDashboardPage";
import PosterHomePage from "@/features/poster/ui/PosterHomePage";
import PosterTaskDetailPage from "@/features/poster/ui/PosterTaskDetailPage";
// V26.4 (2026-05-14) — PosterAssetEditPage 已删, 全部编辑能力进 AssetEditor 三 tab。
// /ai-haibao/tasks/:taskId/assets/:assetId/edit 收旧 bookmark/外链, 自动跳详情页。
function RedirectToTaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  return <Navigate to={taskId ? `/ai-haibao/tasks/${taskId}` : "/ai-haibao"} replace />;
}
import PosterCreatePage from "@/features/poster/ui/PosterCreatePage";
import VideoHomePage from "@/features/video/ui/VideoHomePage";
import VideoTemplatesPage from "@/features/video/ui/VideoTemplatesPage";
import VideoAssetLibraryPage from "@/features/video/ui/VideoAssetLibraryPage";
import VideoCreatePage from "@/features/video/ui/VideoCreatePage";
import VideoRealCreatePage from "@/features/video/ui/VideoRealCreatePage";
import VideoTaskDetailPage from "@/features/video/ui/VideoTaskDetailPage";
import DigitalHumanHomePage from "@/features/digital-human/ui/DigitalHumanHomePage";
import ReporterHubPage from "@/pages/reporter/ReporterHubPage";
import ReporterCreatePage from "@/pages/reporter/ReporterCreatePage";
import ReporterWorkbenchPage from "@/pages/reporter/ReporterWorkbenchPage";
import ReporterPreviewPage from "@/pages/reporter/ReporterPreviewPage";
import DhCreateRedirect from "@/features/digital-human/ui/DhCreateRedirect";
import { DhPhotoPage, DhTalkingPage, DhTtsPage } from "@/features/digital-human/ui/DhLinePages";
import DigitalHumanTaskDetailPage from "@/features/digital-human/ui/DigitalHumanTaskDetailPage";
import DhShowcaseGalleryPage from "@/features/digital-human/ui/DhShowcaseGalleryPage";
import CharacterLibraryPage from "@/features/characters/ui/CharacterLibraryPage";
import CharacterDetailPage from "@/features/characters/ui/CharacterDetailPage";
import CharacterRealCreatePage from "@/features/characters/ui/CharacterRealCreatePage";
import CharacterVirtualCreatePage from "@/features/characters/ui/CharacterVirtualCreatePage";
import VoiceLibraryPage from "@/features/digital-human/ui/VoiceLibraryPage";
import { VoiceRecordEnrollCard } from "@/features/digital-human/ui/components/VoiceRecordEnrollCard";
import WorksPage from "./pages/works/WorksPage";
// 获客舱(ai_acquisition):hub 四子能力 = 地图获客(自研)+ 外呼/邮件/数据获客(GEO-CARDS 外链迁入)。
// 获客舱(2026-09-17 重构):默认落地页从「五张卡的工具墙」换成「今日商机」——
// 每天自动送到面前的一份单子。地图搜索与外链三卡退成二级入口。
// ★ 同日 v2(契约 §12.8):`/ai-huoke` 改成**落地分流** —— 没订阅也没单子的人
//   落在「去哪里找」(目录),否则才是今日商机。空页面教不会新用户任何事。
//   「信号订阅」整页退役,盯什么搬到每一种发现自己的卡上;旧路径重定向到目录。
import AcqLanding from "@/features/acquisition/ui/AcqLanding";
import AcqDiscoverCatalogPage from "@/features/acquisition/ui/DiscoverCatalogPage";
import AcqLeadsPage from "@/features/acquisition/ui/LeadsPage";
import AcqCompanyFinderPage from "@/features/acquisition/ui/CompanyFinderPage";
import AcqQikeWorkbenchPage from "@/features/acquisition/ui/QikeWorkbenchPage";
import AcqMyBusinessPage from "@/features/acquisition/ui/MyBusinessPage";
import AcqExternalToolsPage from "@/features/acquisition/ui/ExternalToolsPage";
import PromotionGeoPage from "@/features/acquisition/ui/PromotionGeoPage";
// 建站舱(ai_website,2026-09-22 转正):我的站点 / 模板市场 + 站点级三页(采集 / 编辑 / 域名,带 :id)。
import WebsiteSitesPage from "@/features/website/ui/SitesPage";
import WebsiteTemplatesPage from "@/features/website/ui/TemplatesPage";
import WebsiteCollectPage from "@/features/website/ui/CollectPage";
import WebsiteEditPage from "@/features/website/ui/EditPage";
import WebsiteDomainPage from "@/features/website/ui/DomainPage";
import WebsiteSiteNewsPage from "@/pages/website/SiteNewsPage";
// 1010 建站舱流程改造:三段式 生成 / 预览调整 / 上线(原采集 / 编辑 / 域名保留为高级编辑)
import WebsiteGeneratePage from "@/features/website/ui/GeneratePage";
import WebsitePreviewPage from "@/features/website/ui/PreviewPage";
import WebsiteLaunchPage from "@/features/website/ui/LaunchPage";
import MapLeadPage from "@/features/map-lead/ui/MapLeadPage";
import GeoRadarPage from "@/features/geo-radar/ui/GeoRadarPage";
import GeoReportDetailPage from "@/features/geo-radar/ui/GeoReportDetailPage";
import BizAdvisorPage from "@/features/biz-advisor/ui/BizAdvisorPage";
import Forbidden from "./pages/Forbidden.tsx";
import { PartnerRoute } from "@/app/guards/PartnerRoute";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/widgets/ImpersonationBanner";

// ── lazy(P-5):rare / 平台域 / admin 工具,首屏不需要 ──────────────
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage.tsx"));
const PermissionManagement = lazy(
  () => import("@/features/permissions/ui/PermissionManagementPage")
);
const Profile = lazy(() => import("@/features/profile/ui/ProfilePage"));
// 帮助中心(2026-09-19 §21):新手指引 + 更新记录。典型「不会用了才来」的页,不在首屏路径上 → lazy。
const HelpPage = lazy(() => import("./pages/help/HelpPage.tsx"));
const ChangelogPage = lazy(() => import("./pages/help/ChangelogPage.tsx"));
const TenantSettings = lazy(() => import("@/features/tenant-settings/ui/TenantSettingsPage"));
const UserManagement = lazy(() => import("@/features/tenant-users/ui/UserManagementPage"));
// 经营看板(2026-07-30 四合一):看盘/对话记录/记忆/技能/记忆审计五个躯干收成一页 tab,
// 原独立路由(含 /growth-cockpit、/advisor-team-board、/advisor/board)全部重定向进来。
const AdvisorHub = lazy(() => import("@/pages/advisor/AdvisorHubPage"));
const AdvisorChatFull = lazy(() => import("@/pages/advisor/AdvisorChatFullPage"));
const AdvisorDispatches = lazy(() => import("@/features/advisor-dispatches/ui/DispatchReviewPage"));
const TeamTasks = lazy(() => import("@/features/team-tasks/ui/TeamTasksPage"));
// 爆款情报(P-A③):横切输入源(解析→蓝图→路由目的地),非首屏必需 → lazy。
const ExplosivePage = lazy(() => import("@/features/explosive/ui/ExplosivePage"));
// 官媒舱(ai_official_media):媒体采购网资源只读检索(显示阶段,投稿暂缓),非首屏必需 → lazy。
const OfficialMediaPage = lazy(() => import("@/features/official-media/ui/OfficialMediaPage"));
const TenantList = lazy(() => import("@/features/tenant-management/ui/TenantListPage"));
const LongImageBuilderPage = lazy(
  () => import("@/features/poster/longimage/ui/LongImageBuilderPage")
);
// AI 模特换衣三步页(2026-08-12):新场景,不在首屏必经路径上 → lazy 切 chunk。
const TryonCreatePage = lazy(() => import("@/features/poster/ui/TryonCreatePage"));
const TenantDetail = lazy(() => import("@/features/tenant-management/ui/TenantDetailPage"));
const TenantPlans = lazy(() => import("@/features/billing/ui/TenantPlansPage"));
const TenantQuotas = lazy(() => import("@/features/billing/ui/TenantQuotasPage"));
const TenantForm = lazy(() => import("@/features/tenant-management/ui/TenantFormPage"));
const MyBilling = lazy(() => import("@/features/billing/ui/MyBillingPage"));
// 积分明细透明化批(2026-09-02):员工/经理自查页,超管走「我的套餐」→「积分明细」看全租户。
const MyCredits = lazy(() => import("@/features/billing/ui/MyCreditsPage"));
const TenantPlanDetail = lazy(() => import("@/features/billing/ui/TenantPlanDetailPage"));
const TenantPlanNew = lazy(() => import("@/features/billing/ui/TenantPlanNewPage"));
const PosterTemplateAdminPage = lazy(() => import("@/features/poster/ui/PosterTemplateAdminPage"));
const DhShowcaseAdminPage = lazy(() => import("@/features/digital-human/ui/DhShowcaseAdminPage"));
const PosterSceneAdminPage = lazy(() => import("@/features/poster/ui/PosterSceneAdminPage"));
const VideoBgmAdminPage = lazy(() => import("@/features/video/ui/VideoBgmAdminPage"));
const BrandAssetsPage = lazy(() => import("@/features/brand-assets/ui/BrandAssetsPage"));
const ProductTreePage = lazy(() => import("@/features/products/ui/ProductTreePage"));
// D3 maker-checker:KB 抽取产品候选审批台(平台超管代客态)
const KbReviewDeskPage = lazy(() => import("@/pages/KbReviewDeskPage"));
const OrgManagementPage = lazy(() => import("@/features/org-departments/ui/OrgManagementPage"));
const TenantAuditPage = lazy(() =>
  import("@/features/tenant-audit/ui/TenantAuditPage").then((m) => ({ default: m.TenantAuditPage }))
);
const IndustryKb = lazy(() => import("@/features/industry-kb/ui/IndustryKbPage"));
const PlatformAudit = lazy(() => import("@/features/platform-audit/ui/PlatformAuditPage"));
const LlmConfigPage = lazy(() => import("@/features/llm-config/ui/LlmConfigPage"));
const ModelCatalogPage = lazy(() => import("@/features/llm-config/ui/ModelCatalogPage"));
const PlatformChannels = lazy(() => import("@/features/platform-channels/ui/PlatformChannelsPage"));
const PlatformAdmins = lazy(() => import("@/features/platform-admins/ui/PlatformAdminsPage"));
const PartnerListPage = lazy(() => import("@/features/platform-partners/ui/PartnerListPage"));
const PartnerDetailPage = lazy(() => import("@/features/platform-partners/ui/PartnerDetailPage"));
const PlatformSettingsPage = lazy(
  () => import("@/features/platform-partners/ui/PlatformSettingsPage")
);
const PrivateDeploymentListPage = lazy(
  () => import("@/features/platform-partners/ui/private/PrivateDeploymentListPage")
);
const PrivateDeploymentDetailPage = lazy(
  () => import("@/features/platform-partners/ui/private/PrivateDeploymentDetailPage")
);
const LicensePage = lazy(() => import("@/features/platform-license/ui/LicensePage"));
// 1011 算法备案:生成内容审核台 / 敏感词库 / 全局合规配置
const ContentReviewPage = lazy(() => import("@/features/platform-compliance/ui/ContentReviewPage"));
const SensitiveWordsPage = lazy(
  () => import("@/features/platform-compliance/ui/SensitiveWordsPage")
);
const ComplianceSettingsPage = lazy(
  () => import("@/features/platform-compliance/ui/ComplianceSettingsPage")
);
const BillingLedger = lazy(() => import("@/features/billing/ui/BillingLedger"));
const BillingDashboard = lazy(() => import("@/features/billing/ui/BillingDashboard"));
// OEM 渠道后台(W4 C-H / C-J):按 PARTNER_PAGES 的 key 分发;租户三页走 tenant-management mode="partner"。
const PartnerPortalPage = lazy(() => import("@/features/partner/ui/PartnerPortalPage"));

// audit-0713 C1a:查询错误零静默的底线兜底(口径与原文脱敏见 onGlobalQueryError 头注)。
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onGlobalQueryError }),
  defaultOptions: {
    queries: {
      retry: 1,
      throwOnError: false,
    },
  },
});

// ADR-0013 S1b — app-shell 路由级布局:AdminLayout 在此(app 层)统一包裹,
// 业务页不再自包裹 <AdminLayout>(features→app/widgets 违 FSD 单向),只返回内容。
/** 1004 O5:直营专属页(更新记录)—— 渠道 / 中性品牌直链进来也回 fallback。 */
function DirectBrandOnly({ children, fallback }: { children: React.ReactNode; fallback: string }) {
  const brand = useBrand();
  return showsReleaseNotes(brand) ? <>{children}</> : <Navigate to={fallback} replace />;
}

function P({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ProtectedRoute>
      <AdminLayout>
        <PageErrorBoundary key={pathname}>{children}</PageErrorBoundary>
      </AdminLayout>
    </ProtectedRoute>
  );
}

import type { ExpectedScope } from "@/contexts/AuthContext";

// 默认 AUTO:主域 index.html 兼容入口,登录时按平台→租户顺序自动尝试两个端点。
// platformMain.tsx / tenantMain.tsx 显式覆盖为 PLATFORM / TENANT 严格模式。
const App = ({ expectedScope = "AUTO" }: { expectedScope?: ExpectedScope }) => {
  const { t } = useTranslation();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ConfirmProvider>
          <BrowserRouter future={{ v7_relativeSplatPath: true }}>
            <AuthProvider expectedScope={expectedScope}>
              <ImpersonationProvider>
                <ImpersonationBanner />
                <Suspense fallback={<TopLoader loading text={t("loaders.loadingPage")} />}>
                  <Routes>
                    {/* 企微 H5 — 独立 OAuth 鉴权，不经过 ProtectedRoute */}
                    <Route path="/wecom/coaching" element={<WeComCoaching />} />
                    <Route path="/login" element={<Login />} />
                    {/* 用户服务协议 / 隐私政策:公开页,登录页底部链过来 */}
                    <Route path="/legal/terms" element={<LegalDocPage kind="terms" />} />
                    <Route path="/legal/privacy" element={<LegalDocPage kind="privacy" />} />
                    {/* 403 自身即为越权降落页,不包 ProtectedRoute */}
                    <Route path="/403" element={<Forbidden />} />
                    <Route
                      path="/"
                      element={
                        <P>
                          <Index />
                        </P>
                      }
                    />
                    {/* /dashboard 作为 / 的别名,满足"登录后跳 /dashboard"的语义,不破坏老路径 */}
                    <Route
                      path="/dashboard"
                      element={
                        <P>
                          <Index />
                        </P>
                      }
                    />
                    {/* 群页(发现流):点大类 /g/:cluster 落该群工作台;非法 key 组件内重定向回首页 */}
                    <Route
                      path="/g/:cluster"
                      element={
                        <P>
                          <ClusterPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-kefu"
                      element={
                        <P>
                          <AiKefu />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-peilian"
                      element={
                        <P>
                          <AiPeilian />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao"
                      element={
                        <P>
                          <PosterDashboardPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao/templates"
                      element={
                        <P>
                          <PosterHomePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao/v3"
                      element={<Navigate to="/ai-haibao/templates" replace />}
                    />
                    {/* UX-1 (2026-05-30) — 统一创建页(文字/模板/图改图)。
                        /new 保留为 alias(保住 ?draftId= 草稿继续 + prefillPrompt state)。 */}
                    <Route
                      path="/ai-haibao/create"
                      element={
                        <P>
                          <PosterCreatePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao/detail-long"
                      element={
                        <P>
                          <LongImageBuilderPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao/new"
                      element={
                        <P>
                          <PosterCreatePage />
                        </P>
                      }
                    />
                    {/* AI 模特换衣(2026-08-12):自己的三步页,出图后落海报详情页 /tasks/:id */}
                    <Route
                      path="/ai-haibao/tryon"
                      element={
                        <P>
                          <TryonCreatePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-haibao/tasks/:id"
                      element={
                        <P>
                          <PosterTaskDetailPage />
                        </P>
                      }
                    />
                    {/* V26.4 (2026-05-14) — /edit 路由删除, 重定向回任务详情 */}
                    <Route
                      path="/ai-haibao/tasks/:taskId/assets/:assetId/edit"
                      element={<RedirectToTaskDetail />}
                    />
                    {/* V38 (2026-05-28) — AI视频模块 P0 */}
                    <Route
                      path="/ai-video"
                      element={
                        <P>
                          <VideoHomePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-video/templates"
                      element={
                        <P>
                          <VideoTemplatesPage />
                        </P>
                      }
                    />
                    {/* W3 视频资产库(动作库/环境床):挂 /ai-video 下 = 白名单/模块门零新增
                        (与「平台 bgm 嵌 /video 下」同思路;后端同在 /api/v1/video/** 三角色门)。 */}
                    <Route
                      path="/ai-video/assets"
                      element={
                        <P>
                          <VideoAssetLibraryPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-video/create"
                      element={
                        <P>
                          <VideoCreatePage />
                        </P>
                      }
                    />
                    {/* SEEDANCE 真人出镜 P0.5(0915 车 F):专属页,两步式选人+选玩法,
                        真人出镜卡 openLiveAction 改指这里(cast=real 通用线仍在 /ai-video/create
                        保留作编排底座,不撤)。静态段排在 /ai-video/create 后无所谓——两段路径
                        字面不重叠,不存在 /characters/new-real 那种静态段必须排在动态段前的坑。 */}
                    <Route
                      path="/ai-video/real"
                      element={
                        <P>
                          <VideoRealCreatePage />
                        </P>
                      }
                    />
                    {/* 爆款情报(P-A③,module_key ai_explosive_parse):解析→拆解报告→路由屏 + 蓝图库 */}
                    <Route
                      path="/ai-explosive"
                      element={
                        <P>
                          <ExplosivePage />
                        </P>
                      }
                    />
                    {/* 官媒舱(显示阶段,module_key ai_official_media):媒体采购网资源只读检索(投稿暂缓) */}
                    <Route
                      path="/ai-official-media"
                      element={
                        <P>
                          <OfficialMediaPage />
                        </P>
                      }
                    />
                    {/* 官媒舱(R2,module_key ai_reporter):体裁卡墙 → 创建 → 工作台 → 交付 */}
                    <Route
                      path="/ai-jizhe"
                      element={
                        <P>
                          <ReporterHubPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-jizhe/create"
                      element={
                        <P>
                          <ReporterCreatePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-jizhe/workbench/:id"
                      element={
                        <P>
                          <ReporterWorkbenchPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-jizhe/preview/:id"
                      element={
                        <P>
                          <ReporterPreviewPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-video/tasks/:id"
                      element={
                        <P>
                          <VideoTaskDetailPage />
                        </P>
                      }
                    />
                    {/* S9-A (2026-07-03) — 数字人口播 A */}
                    <Route
                      path="/ai-digital-human"
                      element={
                        <P>
                          <DigitalHumanHomePage />
                        </P>
                      }
                    />
                    {/* 2026-08-07 IA 重构:一卡一页。三条线各一个路由;
                        老 /create 留跳板(带 state 转,老收藏与各处「送去数字人」不断)。 */}
                    <Route
                      path="/ai-digital-human/talking"
                      element={
                        <P>
                          <DhTalkingPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-digital-human/photo"
                      element={
                        <P>
                          <DhPhotoPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-digital-human/tts"
                      element={
                        <P>
                          <DhTtsPage />
                        </P>
                      }
                    />
                    <Route path="/ai-digital-human/create" element={<DhCreateRedirect />} />
                    <Route
                      path="/ai-digital-human/tasks/:id"
                      element={
                        <P>
                          <DigitalHumanTaskDetailPage />
                        </P>
                      }
                    />
                    {/* packs W2(设计 §2.4)— 「我的资产」收编为人物库真人原料区:旧路由重定向到人物库。
                        形象/声纹管线契约不动,页面归属并入人物详情「真人原料」Tab。 */}
                    <Route
                      path="/ai-digital-human/my-assets"
                      element={<Navigate to="/characters" replace />}
                    />
                    {/* 2026-08-10 声音库恢复独立页:卡墙「声纹克隆」卡 route 指这里,此前 302 到
                        人物库墙 → 用户被推去老板 IP 图片采集流,声纹克隆反而没了直达入口。
                        声纹不依赖人物(授权入库后全公司共用、录音克隆须本人、跨公司隔离、删除仅本人或管理员),
                        人物详情「真人原料」内嵌形态不动。 */}
                    <Route
                      path="/ai-digital-human/voices"
                      element={
                        <P>
                          <VoiceLibraryPage />
                        </P>
                      }
                    />
                    {/* ★素材库独立页退役,302 回人物库(0915 归一批 B)。
                        归一后「素材属于人物」—— 不存在无主素材,也就没有"所有素材的列表"
                        这个页面该有的东西:它当初存在,是因为形象表与人物只有一条可空的反向
                        指针,形象可以先于人物存在。素材现在从落库那一刻起就属于某个人物,
                        管理入口自然回到人物详情页「这个人长什么样」那一块。
                        路由保留做 302,老书签/老卡片不落 404。 */}
                    <Route
                      path="/ai-digital-human/avatars"
                      element={<Navigate to="/characters" replace />}
                    />
                    {/* packs W2(2026-07-27)— 人物库(一等库):库墙 + 详情(slot 物料网格 + 老板 IP 流)。 */}
                    <Route
                      path="/characters"
                      element={
                        <P>
                          <CharacterLibraryPage />
                        </P>
                      }
                    />
                    {/* 静态段 new-real / new-virtual 必须排在 :id 前语义(v6 按特异度排名,静态段天然压过动态段)。 */}
                    <Route
                      path="/characters/new-virtual"
                      element={
                        <P>
                          <CharacterVirtualCreatePage />
                        </P>
                      }
                    />
                    <Route
                      path="/characters/new-real"
                      element={
                        <P>
                          {/* 第 4 步录音卡路由级注入(FSD:characters 与 digital-human 互不 import,
                              组合在 app 层,同下面 realAssetsPanel 范式)。 */}
                          <CharacterRealCreatePage
                            voiceEnrollSlot={(onEnrolled) => (
                              <VoiceRecordEnrollCard onEnrolled={(p) => onEnrolled(Number(p.id))} />
                            )}
                          />
                        </P>
                      }
                    />
                    <Route
                      path="/characters/:id"
                      element={
                        <P>
                          {/* ★0915 归一批 A:「真人原料」Tab 连同它的路由级注入槽一并退役 ——
                              那个 Tab 把整个素材库页原样嵌进人物详情,且不按人物过滤(人物页
                              列出的是当前登录人名下全部素材,与本人物无关)。素材库仍有独立页
                              `/ai-digital-human/avatars`。详情页现为两块上下排,无需注入。 */}
                          <CharacterDetailPage />
                        </P>
                      }
                    />
                    {/* S9-D (2026-07-04) — 样片墙(平台策展 recipe 橱窗,租户一键套用) */}
                    <Route
                      path="/ai-digital-human/showcase"
                      element={
                        <P>
                          <DhShowcaseGalleryPage />
                        </P>
                      }
                    />
                    {/* 经营增长驾驶舱(S4/W3.3:聚合雷达+诊断概览 + 军师记忆 tab;meta 页,角色门 MANAGER+)。 */}
                    {/* F1-a(2026-07-24 IA 二次收口)—— 「我做过什么」的唯一落点:三线聚合。
                        ★不挂单一 moduleKey 门:它按线逐条降级(无该模块权限 = 该线不出现),
                        只要有任意一条创作线就该进得来;三线全无权限时页内自己出空态。 */}
                    <Route
                      path="/works"
                      element={
                        <P>
                          <WorksPage />
                        </P>
                      }
                    />
                    {/* 老深链(书签/企微里发过的链接)→ 经营看板对应 tab。走 <P> 让守卫先判角色,
                        重定向不能用来绕过 roleRouteAccess/deny。 */}
                    <Route
                      path="/growth-cockpit"
                      element={
                        <P>
                          <Navigate to="/advisor?tab=board" replace />
                        </P>
                      }
                    />
                    {/* 可见度雷达 GEO(MVP;A4 接 RBAC module gate)。 */}
                    <Route
                      path="/geo-radar"
                      element={
                        <P>
                          <GeoRadarPage />
                        </P>
                      }
                    />
                    {/* E0:可见度报告独立详情页(守卫 startsWith 已覆盖子路由+module gate)。 */}
                    <Route
                      path="/geo-radar/runs/:id"
                      element={
                        <P>
                          <GeoReportDetailPage />
                        </P>
                      }
                    />
                    {/* 经营诊断(原「增长教练」正名;biz_advisor module gate)。 */}
                    <Route
                      path="/biz-advisor"
                      element={
                        <P>
                          <BizAdvisorPage />
                        </P>
                      }
                    />
                    {/* V11.3 (2026-05-12) — 3 个 admin 子页面合到父模块"AI海报", 新路径 /platform/poster-admin/* */}
                    <Route
                      path="/platform/poster-admin"
                      element={<Navigate to="/platform/poster-admin/templates" replace />}
                    />
                    <Route
                      path="/platform/poster-admin/templates"
                      element={
                        <P>
                          <PosterTemplateAdminPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/poster-admin/scenes"
                      element={
                        <P>
                          <PosterSceneAdminPage />
                        </P>
                      }
                    />
                    {/* S9-D (2026-07-04) — 数字人样片墙平台维护(PLATFORM_ADMIN) */}
                    <Route
                      path="/platform/dh-showcase-admin"
                      element={
                        <P>
                          <DhShowcaseAdminPage />
                        </P>
                      }
                    />
                    {/* V42 BGM 管理 */}
                    <Route
                      path="/platform/video-bgms"
                      element={
                        <P>
                          <VideoBgmAdminPage />
                        </P>
                      }
                    />
                    {/* 旧路径 redirect 兼容 bookmark / sidebar 缓存 */}
                    <Route
                      path="/platform/poster-templates"
                      element={<Navigate to="/platform/poster-admin/templates" replace />}
                    />
                    <Route
                      path="/platform/poster-scenes"
                      element={<Navigate to="/platform/poster-admin/scenes" replace />}
                    />
                    {/* cutover:/platform/poster-styles 退役(风格字典移除),旧 bookmark 落模板页 */}
                    <Route
                      path="/platform/poster-styles"
                      element={<Navigate to="/platform/poster-admin/templates" replace />}
                    />
                    <Route
                      path="/platform/poster-admin/styles"
                      element={<Navigate to="/platform/poster-admin/templates" replace />}
                    />
                    <Route
                      path="/tenant-admin/brand-assets"
                      element={
                        <P>
                          <BrandAssetsPage />
                        </P>
                      }
                    />
                    <Route
                      path="/coming-soon/:feature"
                      element={
                        <P>
                          <ComingSoonPage />
                        </P>
                      }
                    />
                    <Route
                      path="/knowledge"
                      element={
                        <P>
                          <KnowledgeBase />
                        </P>
                      }
                    />
                    <Route
                      path="/products"
                      element={
                        <P>
                          <ProductTreePage />
                        </P>
                      }
                    />
                    <Route
                      path="/products/proposals"
                      element={
                        <P>
                          <KbReviewDeskPage />
                        </P>
                      }
                    />
                    {/* K-3 正名路由:同一审批台,新入口(平台侧栏「知识审核」/ 租户 KB 页「去审核台」);
                        /products/proposals 保留兼容既有链接。 */}
                    <Route
                      path="/kb/review"
                      element={
                        <P>
                          <KbReviewDeskPage />
                        </P>
                      }
                    />
                    <Route
                      path="/org-departments"
                      element={
                        <P>
                          <OrgManagementPage />
                        </P>
                      }
                    />
                    {/* 2026-09-16:租户自己的操作审计(ADMIN)。此前只有平台侧入口 —— 租户问
                        「谁把那份报价单下载走了」只能来找我们查。 */}
                    <Route
                      path="/tenant-audit"
                      element={
                        <P>
                          <TenantAuditPage />
                        </P>
                      }
                    />
                    <Route
                      path="/permissions"
                      element={
                        <P>
                          <PermissionManagement />
                        </P>
                      }
                    />
                    <Route
                      path="/channels"
                      element={
                        <P>
                          <ChannelManagement />
                        </P>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <P>
                          <Profile />
                        </P>
                      }
                    />
                    {/* 帮助中心(§21.1):/help 新手指引 · /help/changelog 更新记录。
                        全员可进(无 moduleKey / roles 门)—— 「看不懂怎么用」跟他开通了哪几个舱无关。 */}
                    <Route
                      path="/help"
                      element={
                        <P>
                          <HelpPage />
                        </P>
                      }
                    />
                    <Route
                      path="/help/changelog"
                      element={
                        <P>
                          {/* 1004 O5:渠道租户不露更新记录(直链也回帮助页) */}
                          <DirectBrandOnly fallback="/help">
                            <ChangelogPage />
                          </DirectBrandOnly>
                        </P>
                      }
                    />
                    <Route
                      path="/ai-huoke"
                      element={
                        <P>
                          <AcqLanding />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-huoke/discover"
                      element={
                        <P>
                          <AcqDiscoverCatalogPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-huoke/leads"
                      element={
                        <P>
                          <AcqLeadsPage />
                        </P>
                      }
                    />
                    {/* 老路径兼容:信号订阅页退役,盯什么搬到发现目录的每一张卡上。
                        ★ 留重定向不留 404:这个地址在企微推送文案、老书签和上一版的
                        空态按钮里都出现过,断掉它等于把那些入口一起打死。 */}
                    <Route
                      path="/ai-huoke/signals"
                      element={<Navigate to="/ai-huoke/discover" replace />}
                    />
                    <Route
                      path="/ai-huoke/business"
                      element={
                        <P>
                          <AcqMyBusinessPage />
                        </P>
                      }
                    />
                    {/* 外链三卡(GEO-CARDS 反代机制原样保留),商机卡动作按钮的落点 */}
                    <Route
                      path="/ai-huoke/tools"
                      element={
                        <P>
                          <AcqExternalToolsPage />
                        </P>
                      }
                    />
                    {/* 1015 企业库找客户(P2):按行业 / 地区 / 标签筛工商登记的公司,与地图获客平级 */}
                    <Route
                      path="/ai-huoke/company"
                      element={
                        <P>
                          <AcqCompanyFinderPage />
                        </P>
                      }
                    />
                    {/* 1025 拓客工作台:嵌入启客拓客,解锁的企业回推进线索库 */}
                    <Route
                      path="/ai-huoke/qike"
                      element={
                        <P>
                          <AcqQikeWorkbenchPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-huoke/map"
                      element={
                        <P>
                          <MapLeadPage />
                        </P>
                      }
                    />
                    {/* 推广舱过渡软链(1012):后端仍 coming_soon,点卡直接免密进 GEO 管理系统 */}
                    <Route
                      path="/ai-promotion"
                      element={
                        <P>
                          <PromotionGeoPage />
                        </P>
                      }
                    />
                    {/* 建站舱(ai_website,2026-09-22 转正):站点级页面带 :id */}
                    <Route
                      path="/ai-website"
                      element={
                        <P>
                          <WebsiteSitesPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/templates"
                      element={
                        <P>
                          <WebsiteTemplatesPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/generate"
                      element={
                        <P>
                          <WebsiteGeneratePage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/preview"
                      element={
                        <P>
                          <WebsitePreviewPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/launch"
                      element={
                        <P>
                          <WebsiteLaunchPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/collect"
                      element={
                        <P>
                          <WebsiteCollectPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/edit"
                      element={
                        <P>
                          <WebsiteEditPage />
                        </P>
                      }
                    />
                    <Route
                      path="/ai-website/sites/:id/domain"
                      element={
                        <P>
                          <WebsiteDomainPage />
                        </P>
                      }
                    />
                    {/* 1009 新闻舱 × 官网:站点「新闻栏」(已发 / 已排期,跳新闻舱发稿) */}
                    <Route
                      path="/ai-website/sites/:id/news"
                      element={
                        <P>
                          <WebsiteSiteNewsPage />
                        </P>
                      }
                    />
                    <Route
                      path="/tenant-settings"
                      element={
                        <P>
                          <TenantSettings />
                        </P>
                      }
                    />
                    {/* 老路径兼容:/settings → 个人中心 */}
                    <Route path="/settings" element={<Navigate to="/profile" replace />} />
                    <Route
                      path="/users"
                      element={
                        <P>
                          <UserManagement />
                        </P>
                      }
                    />
                    {/* B1(2026-08-17):军师满屏对话页 —— agent_access 即可(STAFF 也能用,页内自门控)。
                        放在 /advisor 之前,精确路径优先命中,不被 /advisor 顶层吃掉。 */}
                    <Route
                      path="/advisor/chat"
                      element={
                        <P>
                          <AdvisorChatFull />
                        </P>
                      }
                    />
                    {/* 经营看板:军师一页收口 —— 看盘(驾驶舱/团队看板)· 对话记录 · 记忆 · 技能 · 记忆审计。 */}
                    <Route
                      path="/advisor"
                      element={
                        <P>
                          <AdvisorHub />
                        </P>
                      }
                    />
                    {/* 老卡落点(V25 的角色分流中转)→ 直接落页,由 resolveHubTab 回落首个可见 tab
                        (ADMIN 驾驶舱 / MANAGER 团队看板),角色分流的语义原样保住,不用中转页。 */}
                    <Route
                      path="/advisor/board"
                      element={
                        <P>
                          <Navigate to="/advisor" replace />
                        </P>
                      }
                    />
                    {/* 三条老路由 → 主页对应 tab(2026-07-29 V25):深链/收藏/外部文档不断。
                        走 <P> 是有意的 —— 守卫先判(MANAGER 敲 /advisor-conversations 仍 403,
                        不能靠重定向绕过 roleRouteDeny 把他放进对话审计 tab)。 */}
                    <Route
                      path="/advisor-conversations"
                      element={
                        <P>
                          <Navigate to="/advisor?tab=conversations" replace />
                        </P>
                      }
                    />
                    <Route
                      path="/advisor-skills"
                      element={
                        <P>
                          <Navigate to="/advisor?tab=settings" replace />
                        </P>
                      }
                    />
                    <Route
                      path="/advisor-memory"
                      element={
                        <P>
                          <Navigate to="/advisor?tab=settings" replace />
                        </P>
                      }
                    />
                    <Route
                      path="/advisor-team-board"
                      element={
                        <P>
                          <Navigate to="/advisor?tab=board" replace />
                        </P>
                      }
                    />
                    <Route
                      path="/advisor-dispatches"
                      element={
                        <P>
                          <AdvisorDispatches />
                        </P>
                      }
                    />
                    <Route
                      path="/team-tasks"
                      element={
                        <P>
                          <TeamTasks />
                        </P>
                      }
                    />
                    <Route
                      path="/tenants"
                      element={
                        <P>
                          <TenantList />
                        </P>
                      }
                    />
                    <Route
                      path="/tenants/new"
                      element={
                        <P>
                          <TenantForm />
                        </P>
                      }
                    />
                    <Route
                      path="/tenants/:id/edit"
                      element={
                        <P>
                          <TenantForm />
                        </P>
                      }
                    />
                    <Route
                      path="/tenants/:id"
                      element={
                        <P>
                          <TenantDetail />
                        </P>
                      }
                    />
                    <Route
                      path="/tenant-plans"
                      element={
                        <P>
                          <TenantPlans />
                        </P>
                      }
                    />
                    <Route
                      path="/tenant-plans/new"
                      element={
                        <P>
                          <TenantPlanNew />
                        </P>
                      }
                    />
                    <Route
                      path="/tenant-plans/:planCode"
                      element={
                        <P>
                          <TenantPlanDetail />
                        </P>
                      }
                    />
                    <Route
                      path="/tenant-quotas"
                      element={
                        <P>
                          <TenantQuotas />
                        </P>
                      }
                    />
                    <Route
                      path="/my-billing"
                      element={
                        <P>
                          <MyBilling />
                        </P>
                      }
                    />
                    <Route
                      path="/my-credits"
                      element={
                        <P>
                          <MyCredits />
                        </P>
                      }
                    />
                    <Route
                      path="/admin/billing-ledger"
                      element={
                        <P>
                          <BillingLedger />
                        </P>
                      }
                    />
                    <Route
                      path="/admin/billing-dashboard"
                      element={
                        <P>
                          <BillingDashboard />
                        </P>
                      }
                    />
                    <Route
                      path="/industry-kb"
                      element={
                        <P>
                          <IndustryKb />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/audit"
                      element={
                        <P>
                          <PlatformAudit />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/llm-config"
                      element={
                        <P>
                          <LlmConfigPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/llm-config/models"
                      element={
                        <P>
                          <ModelCatalogPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/channels"
                      element={
                        <P>
                          <PlatformChannels />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/admins"
                      element={
                        <P>
                          <PlatformAdmins />
                        </P>
                      }
                    />
                    {/* OEM C-I:平台渠道商管理(后端 /api/v1/platform/partners/** 仅 PLATFORM_ADMIN)。 */}
                    <Route
                      path="/platform/partners"
                      element={
                        <P>
                          <PartnerListPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/partners/:code"
                      element={
                        <P>
                          <PartnerDetailPage />
                        </P>
                      }
                    />
                    {/* OEM 渠道后台(W4 C-H):PARTNER_ADMIN 白名单只放 /partner 前缀;清单单源 PARTNER_PAGES。 */}
                    {/* OEM W5:私有化部署(后端 /platform/private-deployments/**)+ 私有化实例授权页。 */}
                    <Route
                      path="/platform/private-deployments"
                      element={
                        <P>
                          <PrivateDeploymentListPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/private-deployments/:id"
                      element={
                        <P>
                          <PrivateDeploymentDetailPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/settings"
                      element={
                        <P>
                          <PlatformSettingsPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/license"
                      element={
                        <P>
                          <LicensePage />
                        </P>
                      }
                    />
                    {/* 1011 算法备案:生成内容审核台 / 敏感词库 / 全局合规配置(后端 /platform/** 仅 PLATFORM_ADMIN) */}
                    <Route
                      path="/platform/content-review"
                      element={
                        <P>
                          <ContentReviewPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/sensitive-words"
                      element={
                        <P>
                          <SensitiveWordsPage />
                        </P>
                      }
                    />
                    <Route
                      path="/platform/compliance"
                      element={
                        <P>
                          <ComplianceSettingsPage />
                        </P>
                      }
                    />
                    {/* OEM 渠道后台(W3 C-G 骨架):PARTNER_ADMIN 白名单只放 /partner 前缀;清单单源 PARTNER_PAGES。 */}
                    {/* OEM 第二批 A4:每页按 requiredPerm 过 PartnerRoute(无权 → /403;暂停 → 只读横幅)。 */}
                    {PARTNER_PAGES.map((p) => (
                      <Route
                        key={p.key}
                        path={p.url}
                        element={
                          <P>
                            <PartnerRoute perm={p.requiredPerm}>
                              {p.key === "tenants" ? (
                                <TenantList mode="partner" />
                              ) : (
                                <PartnerPortalPage page={p.key} />
                              )}
                            </PartnerRoute>
                          </P>
                        }
                      />
                    ))}
                    <Route
                      path="/partner/tenants/new"
                      element={
                        <P>
                          <PartnerRoute perm="TENANT_WRITE">
                            <TenantForm mode="partner" />
                          </PartnerRoute>
                        </P>
                      }
                    />
                    <Route
                      path="/partner/tenants/:id"
                      element={
                        <P>
                          <PartnerRoute perm="TENANT_READ">
                            <TenantDetail mode="partner" />
                          </PartnerRoute>
                        </P>
                      }
                    />
                    <Route
                      path="/partner/settlements/:period"
                      element={
                        <P>
                          <PartnerRoute perm="SETTLEMENT">
                            <PartnerPortalPage page="settlements" />
                          </PartnerRoute>
                        </P>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ImpersonationProvider>
            </AuthProvider>
          </BrowserRouter>
        </ConfirmProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
